// src/modules/app-views/services/pages/Document/tabs/AiInfoTab/SuggestedPricingSection.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Descriptions, Collapse, Tag, Typography, theme } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import { DEFAULT_OPEN_KEYS } from './index';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SuggestedPricingSection = ({ data, aiResponse, isMobile }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const sp = aiResponse?.suggestedPricing;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const hasData = sp && (
    sp.suggestedPrice !== undefined ||
    sp.priceRangeMin !== undefined ||
    sp.profitMargin !== undefined
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!hasData) return null;

  return (
    <Collapse
      className="collapsed-content-fit"
      defaultActiveKey={DEFAULT_OPEN_KEYS.includes('suggested-pricing') ? ['suggested-pricing'] : []}
      style={{ marginBottom: 16 }}
      items={[
        {
          key: 'suggested-pricing',
          label: (
            <Flex align="center" gap={8}>
              <ClockCircleOutlined style={{ color: token.colorPrimary }} />
              <span style={{ fontWeight: 600 }}>{translateX('suggested_pricing')}</span>
            </Flex>
          ),
          showArrow: false,
          children: (
            <Descriptions
              bordered
              size="small"
              column={1}
              layout={isMobile ? 'vertical' : 'horizontal'}
              styles={{ label: { fontWeight: 500 } }}
            >
              {sp.suggestedPrice !== undefined && (
                <Descriptions.Item label={translateX('suggested_price')}>
                  {formatCurrency(sp.suggestedPrice)}
                </Descriptions.Item>
              )}
              {(sp.priceRangeMin !== undefined || sp.priceRangeMax !== undefined) && (
                <Descriptions.Item label={translateX('price_range')}>
                  {formatCurrency(sp.priceRangeMin)} - {formatCurrency(sp.priceRangeMax)}
                </Descriptions.Item>
              )}
              {sp.profitMargin !== undefined && (
                <Descriptions.Item label={translateX('profit_margin')}>
                  {typeof sp.profitMargin === 'number' ? `${sp.profitMargin}%` : sp.profitMargin}
                </Descriptions.Item>
              )}
              {sp.priceConfidence && (
                <Descriptions.Item label={translateX('price_confidence')}>
                  <Tag color={sp.priceConfidence === 'high' ? 'green' : sp.priceConfidence === 'medium' ? 'orange' : 'red'}>
                    {sp.priceConfidence?.toUpperCase()}
                  </Tag>
                </Descriptions.Item>
              )}
              {sp.priceNotes && (
                <Descriptions.Item label={translateX('price_notes')}>
                  <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.5 }}>
                    {sp.priceNotes}
                  </Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          ),
        },
      ]}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SuggestedPricingSection;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function formatCurrency(value) {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}
